import React, { useState, useEffect } from 'react';
import { Modal, Tooltip, Switch, Input, Button, Pagination, Radio } from "antd";
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../axios/axiosInstance";
import { MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  ActionButton,
  DataTable,
  FilterPills,
  PageCard,
  PageShell,
  PageToolbar,
  RowIconButton,
  StatusBadge,
} from "../components/ui";
import AdditionalServices from "./AdditionalServices/AdditionalServices";

const Services = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'extra' ? 'extra' : 'primary';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [editServiceId, setEditServiceId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [serviceType, setServiceType] = useState('all');
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchServices(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const fetchServices = async (page, size) => {
    try {
      const response = await axiosInstance.get(`/admin/service?page=${page}&per_page=${size}`);
      setServices(response.data.data);
      setTotalItems(response.data.meta.total);
    } catch (error) {
      console.error("Ошибка при получении списка сервисов:", error);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditServiceId(null);
    setNewServiceName('');
    setNewServicePrice('');
    setIsPrimary(false);
    setServiceType('all');
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const serviceData = {
        name: newServiceName,
        price: newServicePrice,
        primary: isPrimary ? 1 : 0,
        type: serviceType
      };

      if (editServiceId) {
        await axiosInstance.put(`/admin/service/${editServiceId}`, serviceData);
      } else {
        await axiosInstance.post("/admin/service", serviceData);
      }
      fetchServices(currentPage, pageSize);
      closeModal();
    } catch (error) {
      console.error("Ошибка при сохранении сервиса:", error);
    }
  };

  const handleEdit = (service) => {
    setEditServiceId(service.id);
    setNewServiceName(service.name);
    setNewServicePrice(service.price);
    setIsPrimary(service.primary);
    setServiceType(service.type || 'all');
    openModal();
  };

  const handleDelete = async (serviceId) => {
    try {
      await axiosInstance.delete(`/admin/service/${serviceId}`);
      fetchServices(currentPage, pageSize);
    } catch (error) {
      console.error("Ошибка при удалении сервиса:", error);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredServices = services.filter(service =>
      service.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const getTypeName = (type) => {
    switch (type) {
      case 'all':
        return t('status.all');
      case 'ambulatory':
        return t('status.ambulatory');
      case 'stationary':
        return t('status.stationary');
      default:
        return '';
    }
  };

  const typeTones = {
    all: 'slate',
    ambulatory: 'blue',
    stationary: 'violet',
  };

  const renderType = (type) => {
    const label = getTypeName(type);
    if (!label) return <span className="text-xs text-slate-400">{t('common.dash')}</span>;
    return <StatusBadge tone={typeTones[type] ?? 'muted'}>{label}</StatusBadge>;
  };

  const renderPrimary = (text) => (
      <StatusBadge tone={text ? 'brand' : 'muted'}>{text ? t('status.primary') : t('status.extra')}</StatusBadge>
  );

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
    },
    {
      title: t('services.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span className="text-sm font-medium text-slate-800">{name}</span>,
    },
    {
      title: t('services.price'),
      dataIndex: 'price',
      key: 'price',
      width: 130,
      render: (price) => (
          <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{price}</span>
      ),
    },
    {
      title: t('services.kind'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: renderType,
    },
    {
      title: t('services.serviceType'),
      dataIndex: 'primary',
      key: 'primary',
      width: 170,
      render: renderPrimary,
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (text, record) => (
          <div className="flex items-center gap-0.5">
            <Tooltip title={t('common.edit')}>
              <RowIconButton onClick={() => handleEdit(record)}>
                <PencilSquareIcon className="w-[18px] h-[18px]" />
              </RowIconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <RowIconButton onClick={() => handleDelete(record.id)}>
                <TrashIcon className="w-[18px] h-[18px]" />
              </RowIconButton>
            </Tooltip>
          </div>
      )
    }
  ];

  const renderMobileCard = (record) => (
      <div>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[#1D2C7B]">{record.name}</span>
            <span className="block text-xs text-slate-500 mt-0.5 tabular-nums">{t('common.id')}: {record.id}</span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <RowIconButton onClick={() => handleEdit(record)} aria-label={t('common.edit')}>
              <PencilSquareIcon className="w-[18px] h-[18px]" />
            </RowIconButton>
            <RowIconButton onClick={() => handleDelete(record.id)} aria-label={t('common.delete')}>
              <TrashIcon className="w-[18px] h-[18px]" />
            </RowIconButton>
          </div>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {renderPrimary(record.primary)}
          {renderType(record.type)}
          <span className="text-sm text-slate-700 tabular-nums">{record.price}</span>
        </div>
      </div>
  );

  return (
      <PageShell>
        <PageCard>
          <PageToolbar
              title={t('nav.services')}
              count={tab === 'primary' ? totalItems : undefined}
              filters={
                tab === 'primary' ? (
                <Input
                    allowClear
                    size="small"
                    prefix={<MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />}
                    placeholder={t('services.search')}
                    value={searchText}
                    onChange={e => handleSearch(e.target.value)}
                    className="md:!w-[260px]"
                />
                ) : null
              }
              actions={
                tab === 'primary' ? (
                <ActionButton icon={<PlusIcon className="w-4 h-4" />} onClick={openModal}>
                  {t('services.add')}
                </ActionButton>
                ) : null
              }
          />

          <FilterPills
              value={tab}
              onChange={(value) => setSearchParams(value === 'extra' ? { tab: 'extra' } : {})}
              options={[
                  { value: 'primary', label: t('status.primary') },
                  { value: 'extra', label: t('status.extra') },
              ]}
          />

          {tab === 'extra' ? (
              <AdditionalServices embedded />
          ) : (
          <>
          <DataTable
              columns={columns}
              dataSource={filteredServices}
              mobileCard={renderMobileCard}
              pagination={false}
              scrollX={900}
          />

          <div className="flex justify-center sm:justify-end border-t border-slate-100 px-3 py-2.5">
            <Pagination
                size="small"
                current={currentPage}
                pageSize={pageSize}
                onChange={page => setCurrentPage(page)}
                total={totalItems}
                showSizeChanger
                onShowSizeChange={(current, size) => setPageSize(size)}
            />
          </div>
          </>
          )}
        </PageCard>

        <Modal
            centered
            width="100%"
            style={{ maxWidth: 520 }}
            title={editServiceId ? t('services.edit') : t('services.create')}
            visible={isModalOpen}
            onCancel={closeModal}
            footer={[
              <Button key="cancel" onClick={closeModal}>
                {t('services.cancel')}
              </Button>,
              <Button key="save" type="primary" onClick={handleSave}>
                {t('services.save')}
              </Button>
            ]}
        >
          <div className="grid grid-cols-1 gap-4">
            <Input
                placeholder={t('services.name')}
                size="large"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
            />
            <Input
                placeholder={t('services.price')}
                size="large"
                value={newServicePrice}
                onChange={(e) => setNewServicePrice(e.target.value)}
            />
          </div>
          <div className="mt-4">
            <Radio.Group buttonStyle="solid" onChange={e => setServiceType(e.target.value)} value={serviceType} className="flex flex-wrap">
              <Radio.Button value="all">{t('status.all')}</Radio.Button>
              <Radio.Button value="ambulatory">{t('status.ambulatory')}</Radio.Button>
              <Radio.Button value="stationary">{t('status.stationary')}</Radio.Button>
            </Radio.Group>
          </div>
          <div className="flex items-center mt-4 gap-4">
            <Switch checked={isPrimary} onChange={(checked) => setIsPrimary(checked)}/>
            <span className="text-sm text-slate-700">{isPrimary ? t('status.primary') : t('status.extra')}</span>
          </div>

        </Modal>
      </PageShell>
  );
};

export default Services;
